import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  Download,
  UserCheck,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Play,
  RotateCcw,
  RefreshCw,
  CreditCard,
  Edit3,
  FileUp,
  Pill
} from 'lucide-react';
import { toast } from 'sonner';
import brandLogo from '@/assets/logo.png';
import { generatePrescriptionPDF } from '@/lib/generatePrescriptionPDF';
import PrescriptionNotesThread from '@/components/prescriptions/PrescriptionNotesThread';
import PrescriptionActivityTimeline from '@/components/prescriptions/PrescriptionActivityTimeline';
import ErpSyncBadge from '@/components/prescriptions/ErpSyncBadge';
import PrescriptionEditModal from '@/components/prescriptions/PrescriptionEditModal';
import PrescriptionDocumentModal from '@/components/prescriptions/PrescriptionDocumentModal';
import PrescriptionMedicineEditModal from '@/components/prescriptions/PrescriptionMedicineEditModal';

const STATUS_VARIANTS = {
  PENDING: { label: 'Pending', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-800 border-blue-300' },
  WAITING: { label: 'Waiting', className: 'bg-purple-100 text-purple-800 border-purple-300' },
  COMPLETED: { label: 'Completed', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELLED: { label: 'Cancelled', className: 'bg-slate-200 text-slate-700 border-slate-300' }
};

const PrescriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Operational users for assignment dropdown
  const [users, setUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [savingAssign, setSavingAssign] = useState(false);

  // Status transition dialog state
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    targetStatus: '',
    title: '',
    reason: '',
    requiresReason: false
  });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [adminDefaultPaymentMode, setAdminDefaultPaymentMode] = useState('');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const [isChangingPaymentMode, setIsChangingPaymentMode] = useState(false);

  // Editability Modals (Phase 1 & Phase 2)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [medicineEditModalOpen, setMedicineEditModalOpen] = useState(false);

  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const res = await api.get('/api/admin/settings');
        const mode = res.data?.data?.default_payment_mode || res.data?.default_payment_mode || '';
        setAdminDefaultPaymentMode(mode);
      } catch (e) {
        // Silently ignore if settings endpoint unreachable or not configured
      }
    };
    fetchAdminSettings();
  }, []);

  const fetchPrescription = useCallback(async () => {
    try {
      const res = await api.get(`/prescriptions/${id}`);
      if (res.data) {
        setPrescription(res.data);
        setSelectedAssignee(res.data.assigned_user_id ? String(res.data.assigned_user_id) : '');

        // Fetch patient details if patient_id exists
        if (res.data.patient_id || res.data.patientId) {
          const pid = res.data.patient_id || res.data.patientId;
          try {
            const pResp = await api.get(`/patients/${pid}`);
            setPatientDetails(pResp.data || null);
          } catch (e) {
            console.warn('Failed to fetch patient details:', e);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching prescription:', err);
      toast.error('Failed to load prescription');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPrescription();
  }, [fetchPrescription]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/users', { params: { status: 'ACTIVE', role: 'USER' } });
        const list = res.data?.data || res.data?.items || (Array.isArray(res.data) ? res.data : []);
        setUsers(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    };
    fetchUsers();
  }, []);

  const handleAssignChange = async () => {
    setSavingAssign(true);
    try {
      const payload = {
        assigned_user_id: selectedAssignee ? parseInt(selectedAssignee, 10) : null
      };
      await api.post(`/prescriptions/${id}/assign`, payload);
      toast.success(selectedAssignee ? 'Prescription assigned successfully' : 'Prescription unassigned');
      fetchPrescription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update assignment');
    } finally {
      setSavingAssign(false);
    }
  };

  const openStatusDialog = (targetStatus, title, requiresReason = false) => {
    setStatusDialog({
      open: true,
      targetStatus,
      title,
      reason: '',
      requiresReason
    });
    if (targetStatus === 'COMPLETED') {
      if (adminDefaultPaymentMode) {
        setSelectedPaymentMode(adminDefaultPaymentMode);
        setIsChangingPaymentMode(false);
      } else {
        setSelectedPaymentMode('');
        setIsChangingPaymentMode(true);
      }
    }
  };

  const handleConfirmStatus = async () => {
    if (statusDialog.requiresReason && !statusDialog.reason.trim()) {
      toast.error('A reason is required for this status change');
      return;
    }

    const hasMedicines = (prescription?.medicines?.length || 0) > 0;
    if (statusDialog.targetStatus === 'COMPLETED' && hasMedicines && !selectedPaymentMode) {
      toast.error('Please select a payment mode to complete fulfillment');
      return;
    }

    setUpdatingStatus(true);
    try {
      const payload = {
        status: statusDialog.targetStatus,
        reason: statusDialog.reason.trim() || undefined
      };
      if (statusDialog.targetStatus === 'COMPLETED' && selectedPaymentMode) {
        payload.payment_mode = selectedPaymentMode;
      }
      await api.post(`/prescriptions/${id}/status`, payload);
      toast.success(`Prescription marked as ${statusDialog.targetStatus}`);
      setStatusDialog((prev) => ({ ...prev, open: false }));
      fetchPrescription();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const toDataURL = (url) =>
    fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );

  const handleDownloadPDF = async () => {
    if (!prescription) {
      toast.error('No prescription found');
      return;
    }

    const patientInfo = {
      name: patientDetails?.name || prescription.patient_name || prescription.patientName || 'Patient',
      age: patientDetails?.age || prescription.age || '—',
      sex: patientDetails?.gender || prescription.sex || prescription.gender || '—',
      address: patientDetails?.address || prescription.address || '—',
      phone: patientDetails?.phone || '',
      email: patientDetails?.email || ''
    };

    if (!patientInfo.name) {
      toast.error('Patient name is required to generate PDF');
      return;
    }

    setIsGenerating(true);
    try {
      const logoDataUrl = await toDataURL(brandLogo);
      const medicinesArray = Array.isArray(prescription.medicines)
        ? prescription.medicines
        : prescription.medicines
          ? String(prescription.medicines)
              .split(',')
              .map((m) => m.trim())
              .filter(Boolean)
          : [];

      const medicinesStructured = medicinesArray.map((m) => {
        if (typeof m === 'string') {
          return { name: m, dosage: '', frequency: '', duration: '' };
        }
        return {
          name: m.name || m.medicineName || '',
          dosage: m.dosage || '',
          frequency: m.frequency || '',
          duration: m.duration || ''
        };
      });

      await generatePrescriptionPDF({
        patient_name: patientInfo.name,
        patient_id: prescription.patient_id || prescription.patientId || '—',
        patient_age: patientInfo.age,
        patient_gender: patientInfo.sex,
        patient_address: patientInfo.address,
        patient_phone: patientInfo.phone,
        patient_email: patientInfo.email,
        visit_date: prescription.visit_date || prescription.date || '—',
        diet: prescription.diet || prescription.Diet || '',
        lifestyle: prescription.lifestyle || prescription.Lifestyle || '',
        medicines: medicinesStructured,
        notes: prescription.notes || '',
        follow_up_date: prescription.follow_up_date || prescription.followUpDate || '—',
        clinic: {
          logo: logoDataUrl,
          name: 'Sanjeevani'
        },
        filename: `Prescription-${patientInfo.name}-${prescription.id}.pdf`
      });

      toast.success('Prescription PDF downloaded');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error(error.message || 'Unable to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <Clock className="h-8 w-8 animate-spin mx-auto text-emerald-600 mb-2" />
          <p className="text-slate-500">Loading prescription #{id}...</p>
        </div>
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500 mb-4">Prescription #{id} not found.</p>
        <Button onClick={() => navigate('/admin/prescriptions')}>Back to Prescriptions</Button>
      </div>
    );
  }

  const statusMeta = STATUS_VARIANTS[prescription.workflow_status] || {
    label: prescription.workflow_status || 'Pending',
    className: 'bg-slate-100 text-slate-700'
  };

  const isCompleted = prescription.workflow_status === 'COMPLETED';
  const isCancelled = prescription.workflow_status === 'CANCELLED';
  const canEdit = !isCompleted && !isCancelled;

  return (
    <div className="space-y-6">
      {/* Top Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3.5 sm:gap-4">
        {/* Left: Back button + Title + Badges + Patient Subtitle + Mobile Refresh */}
        <div className="flex items-start justify-between w-full lg:w-auto gap-3">
          <div className="flex items-start gap-2 sm:gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/prescriptions')}
              className="h-9 w-9 shrink-0 mt-0.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
              aria-label="Back to prescriptions"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
                  Prescription #{prescription.id}
                </h1>
                <Badge className={`border text-xs font-medium ${statusMeta.className}`}>
                  {statusMeta.label}
                </Badge>
                <Badge variant="outline" className="text-xs font-normal">
                  {prescription.type || 'Digital'}
                </Badge>
                <ErpSyncBadge
                  status={prescription.erp_sync_status}
                  invoiceNo={prescription.erp_invoice_no}
                />
                {prescription.version && prescription.version > 1 && (
                  <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                    v{prescription.version}
                  </Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Patient: <strong className="font-medium text-foreground">{prescription.patient_name || prescription.patientName || 'Patient'}</strong>
              </p>
            </div>
          </div>

          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchPrescription}
            disabled={loading}
            className="sm:hidden h-9 w-9 shrink-0 ml-1 rounded-lg"
            aria-label="Refresh prescription"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Action Controls Bar */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 w-full lg:w-auto sm:justify-end">
          {/* Desktop Refresh Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={fetchPrescription}
            disabled={loading}
            className="hidden sm:inline-flex h-9 gap-1.5 text-xs font-medium"
            aria-label="Refresh prescription"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {canEdit && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditModalOpen(true)}
                className="h-9 gap-1.5 text-xs font-medium text-emerald-800 border-emerald-300 hover:bg-emerald-50 justify-center"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Edit Details
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMedicineEditModalOpen(true)}
                className="h-9 gap-1.5 text-xs font-medium text-emerald-800 border-emerald-300 hover:bg-emerald-50 justify-center"
              >
                <Pill className="h-3.5 w-3.5" />
                Edit Medicines
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDocumentModalOpen(true)}
                className="h-9 gap-1.5 text-xs font-medium text-blue-800 border-blue-300 hover:bg-blue-50 justify-center"
              >
                <FileUp className="h-3.5 w-3.5" />
                {prescription.file_url ? 'Replace Document' : 'Upload Document'}
              </Button>
            </>
          )}

          {prescription.file_url ? (
            <Button asChild variant="outline" size="sm" className="h-9 text-xs font-medium justify-center">
              <a href={prescription.file_url} target="_blank" rel="noreferrer">
                Open Original File
              </a>
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={isGenerating}
              className="h-9 gap-1.5 text-xs font-medium justify-center"
            >
              <Download className="h-3.5 w-3.5" />
              {isGenerating ? 'Preparing...' : 'Download PDF'}
            </Button>
          )}
        </div>
      </div>

      {/* Admin Action & Assignment Bar */}
      {!isCompleted && (
        <Card className="shadow-sm border border-emerald-200/80 bg-gradient-to-br from-emerald-50/60 to-teal-50/30 dark:from-emerald-950/20 dark:to-teal-950/10 dark:border-emerald-900/40 rounded-xl overflow-hidden">
          <CardContent className="p-3.5 sm:p-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3.5 sm:gap-4">
              {/* Assignment Section */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
                <div className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 shrink-0">
                  <UserCheck className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
                  <span>Assignee:</span>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <select
                    className="h-9 px-3 rounded-lg border border-emerald-300/80 bg-white dark:bg-slate-900 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 w-full sm:min-w-[220px]"
                    value={selectedAssignee}
                    onChange={(e) => setSelectedAssignee(e.target.value)}
                  >
                    <option value="">— Unassigned —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} (@{u.username || u.email})
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={handleAssignChange}
                    disabled={savingAssign}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-9 px-3.5 shrink-0 text-xs font-medium"
                  >
                    {savingAssign ? 'Updating...' : 'Update'}
                  </Button>
                </div>
              </div>

              {/* Subtle Divider */}
              <div className="hidden lg:block h-7 w-px bg-emerald-200 dark:bg-emerald-800/60 mx-1" />
              <div className="block lg:hidden border-t border-emerald-200/70 dark:border-emerald-800/40 my-0.5" />

              {/* Admin Workflow Status Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400 shrink-0">
                  Admin Status:
                </span>

                <div className="grid grid-cols-3 sm:flex items-center gap-2 w-full sm:w-auto">
                  {prescription.workflow_status !== 'IN_PROGRESS' && prescription.workflow_status !== 'COMPLETED' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 gap-1 text-blue-700 border-blue-200 hover:bg-blue-50 text-xs font-medium justify-center flex-1 sm:flex-initial"
                      onClick={() => openStatusDialog('IN_PROGRESS', 'Start Processing')}
                    >
                      <Play className="h-3.5 w-3.5" />
                      Start
                    </Button>
                  )}

                  {prescription.workflow_status === 'IN_PROGRESS' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 gap-1 text-purple-700 border-purple-200 hover:bg-purple-50 text-xs font-medium justify-center flex-1 sm:flex-initial"
                      onClick={() => openStatusDialog('WAITING', 'Mark as Waiting', true)}
                    >
                      <AlertCircle className="h-3.5 w-3.5" />
                      Wait
                    </Button>
                  )}

                  {prescription.workflow_status === 'WAITING' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 gap-1 text-blue-700 border-blue-200 hover:bg-blue-50 text-xs font-medium justify-center flex-1 sm:flex-initial"
                      onClick={() => openStatusDialog('IN_PROGRESS', 'Resume Processing')}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Resume
                    </Button>
                  )}

                  {prescription.workflow_status !== 'COMPLETED' && (
                    <Button
                      size="sm"
                      className="h-9 gap-1 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-medium justify-center flex-1 sm:flex-initial"
                      onClick={() => openStatusDialog('COMPLETED', 'Complete Prescription')}
                      data-testid="admin-open-complete-btn"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Complete
                    </Button>
                  )}

                  {prescription.workflow_status !== 'CANCELLED' && prescription.workflow_status !== 'COMPLETED' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 gap-1 text-rose-700 border-rose-200 hover:bg-rose-50 text-xs font-medium justify-center flex-1 sm:flex-initial"
                      onClick={() => openStatusDialog('CANCELLED', 'Cancel Prescription', true)}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clinical Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-card md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle>Patient & Clinical Details</CardTitle>
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditModalOpen(true)}
                className="h-8 gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase font-semibold">Patient</p>
                <p className="font-medium text-slate-900">{prescription.patient_name || prescription.patientName}</p>

                <p className="text-xs text-muted-foreground uppercase font-semibold mt-3">Phone</p>
                <p className="font-medium">{patientDetails?.phone || prescription.phone || '—'}</p>

                <p className="text-xs text-muted-foreground uppercase font-semibold mt-3">Address</p>
                <p className="font-medium">{patientDetails?.address || prescription.address || '—'}</p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground uppercase font-semibold">Visit Date</p>
                <p className="font-medium">{prescription.visit_date || prescription.date || '—'}</p>

                <p className="text-xs text-muted-foreground uppercase font-semibold mt-3">Follow-up</p>
                <p className="font-medium">{prescription.follow_up_date || prescription.followUpDate || '—'}</p>

                <p className="text-xs text-muted-foreground uppercase font-semibold mt-3">Clinical Notes</p>
                <p className="font-medium">{prescription.notes || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Prescription Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">Diet Plan</p>
              <p className="text-sm font-medium">{prescription.diet || prescription.Diet || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">Lifestyle Advice</p>
              <p className="text-sm font-medium">{prescription.lifestyle || prescription.Lifestyle || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">Assigned Staff</p>
              <p className="text-sm font-medium text-emerald-700">
                {prescription.assigned_user_name || 'Unassigned'}
              </p>
            </div>
            {prescription.completed_at && (
              <div>
                <p className="text-xs text-muted-foreground uppercase font-semibold">Completed At</p>
                <p className="text-sm font-medium text-slate-800">
                  {new Date(prescription.completed_at).toLocaleString('en-IN')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Medicines Table */}
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle>Prescribed Medicines</CardTitle>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMedicineEditModalOpen(true)}
              className="h-8 gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
            >
              <Pill className="h-3.5 w-3.5" />
              Edit Medicines
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {Array.isArray(prescription.medicines) && prescription.medicines.length ? (
            <>
              {/* Mobile Card List (< md) */}
              <div className="block md:hidden divide-y divide-border">
                {prescription.medicines.map((m, idx) => {
                  const isErp = Boolean(m.erp_product_id);
                  const dispQty = m.dispensed_quantity !== undefined && m.dispensed_quantity !== null ? m.dispensed_quantity : 1;
                  const dispUnit = m.dispensed_unit || 'UNIT';

                  return (
                    <div key={m.id || idx} className="py-3 first:pt-0 last:pb-0 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900 text-sm">
                          {m.name || m}
                        </div>
                        {isErp ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                            ERP Synced
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                            Manual / Not Synced
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-600 bg-muted/40 p-2 rounded">
                        <div>
                          <span className="text-muted-foreground block text-[10px]">Dispensed</span>
                          <span className="font-medium text-foreground">{dispQty} {dispUnit}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-[10px]">Dosage</span>
                          <span className="font-medium">{m.dosage || '—'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-[10px]">Frequency</span>
                          <span className="font-medium">{m.frequency || '—'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-[10px]">Duration</span>
                          <span className="font-medium">{m.duration || '—'}</span>
                        </div>
                      </div>
                      {m.instructions && (
                        <div className="text-[11px] text-muted-foreground bg-muted/40 px-2.5 py-1.5 rounded">
                          <span className="font-medium text-foreground">Instructions:</span> {m.instructions}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table (md+) */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Medicine Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Dispensed Qty</TableHead>
                      <TableHead>Dosage</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prescription.medicines.map((m, idx) => {
                      const isErp = Boolean(m.erp_product_id);
                      const dispQty = m.dispensed_quantity !== undefined && m.dispensed_quantity !== null ? m.dispensed_quantity : 1;
                      const dispUnit = m.dispensed_unit || 'UNIT';

                      return (
                        <TableRow key={m.id || idx}>
                          <TableCell className="font-medium text-slate-900">
                            <div>{m.name || m}</div>
                            {m.instructions && (
                              <div className="text-[11px] text-muted-foreground mt-0.5">
                                <span className="font-medium text-foreground">Note:</span> {m.instructions}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {isErp ? (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                                ERP Synced
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                                Manual / Not Synced
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            {dispQty} {dispUnit}
                          </TableCell>
                          <TableCell>{m.dosage || '—'}</TableCell>
                          <TableCell>{m.frequency || '—'}</TableCell>
                          <TableCell>{m.duration || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="py-8 text-center border border-dashed border-border rounded-lg space-y-3">
              <p className="text-sm text-muted-foreground">No medicines prescribed (clinical consultation only)</p>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMedicineEditModalOpen(true)}
                  className="text-xs gap-1.5 text-emerald-800 border-emerald-300 hover:bg-emerald-50"
                >
                  <Pill className="h-3.5 w-3.5" />
                  Add Medicines
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Operational Notes Thread & Activity Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PrescriptionNotesThread
          prescriptionId={prescription.id}
          notes={prescription.notes_list || prescription.notesList || []}
          onNoteAdded={fetchPrescription}
        />
        <PrescriptionActivityTimeline
          activities={prescription.activities || prescription.activity || []}
        />
      </div>

      {/* Status Transition Dialog */}
      <Dialog
        open={statusDialog.open}
        onOpenChange={(open) => setStatusDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{statusDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Update prescription #{prescription.id} status to{' '}
              <strong className="text-slate-900">{statusDialog.targetStatus}</strong>.
            </p>
            {statusDialog.requiresReason && (
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">
                  Reason / Operational Note <span className="text-rose-500">*</span>
                </label>
                <Textarea
                  placeholder="Explain why this status is being applied..."
                  value={statusDialog.reason}
                  onChange={(e) => setStatusDialog((prev) => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  className="resize-none"
                />
              </div>
            )}

            {statusDialog.targetStatus === 'COMPLETED' && (
              (prescription?.medicines?.length || 0) === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                  <div className="font-semibold text-slate-800">Clinical-Only Completion</div>
                  <p className="text-slate-600">
                    This prescription contains 0 medicines. Completing it will finalize the clinical consultation record without ERP billing or inventory deduction.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 pt-1">
                  {adminDefaultPaymentMode && !isChangingPaymentMode ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <span className="text-slate-500 font-medium">Payment Mode</span>
                          <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                            <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-800 font-semibold" data-testid="admin-preselected-payment-mode">
                              {selectedPaymentMode}
                            </Badge>
                            <span className="text-slate-500 text-xs font-normal">(Clinic Default)</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs text-emerald-800 border-emerald-300 hover:bg-emerald-100/60"
                          onClick={() => setIsChangingPaymentMode(true)}
                          data-testid="admin-change-payment-mode-btn"
                        >
                          Change
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Preselected from clinic settings. Changing it applies only to this prescription.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label htmlFor="admin-payment-mode-select" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                          Payment Mode <span className="text-rose-500">*</span>
                        </label>
                        {adminDefaultPaymentMode && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-1.5 text-[11px] text-slate-500 hover:text-slate-800"
                            onClick={() => {
                              setSelectedPaymentMode(adminDefaultPaymentMode);
                              setIsChangingPaymentMode(false);
                            }}
                          >
                            Reset to Default ({adminDefaultPaymentMode})
                          </Button>
                        )}
                      </div>
                      <Select
                        value={selectedPaymentMode}
                        onValueChange={setSelectedPaymentMode}
                      >
                        <SelectTrigger id="admin-payment-mode-select" data-testid="admin-payment-mode-select" className="h-10">
                          <SelectValue placeholder="Select payment mode (Required)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CASH">Cash</SelectItem>
                          <SelectItem value="CARD">Card</SelectItem>
                          <SelectItem value="UPI">UPI</SelectItem>
                          <SelectItem value="NET_BANKING">Net Banking</SelectItem>
                        </SelectContent>
                      </Select>
                      {!adminDefaultPaymentMode && (
                        <p className="text-[11px] text-amber-700">
                          No clinic default configured. Please select a payment mode to complete fulfillment.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setStatusDialog((prev) => ({ ...prev, open: false }))}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmStatus}
              disabled={updatingStatus || (statusDialog.targetStatus === 'COMPLETED' && (prescription?.medicines?.length || 0) > 0 && !selectedPaymentMode)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="admin-confirm-status-btn"
            >
              {updatingStatus ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription Edit & Document Replacement Modals */}
      <PrescriptionEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        prescription={prescription}
        patient={patientDetails}
        onSuccess={() => {
          fetchPrescription();
        }}
        onConflict={fetchPrescription}
      />
      <PrescriptionDocumentModal
        open={documentModalOpen}
        onOpenChange={setDocumentModalOpen}
        prescription={prescription}
        onSuccess={() => {
          fetchPrescription();
        }}
        onConflict={fetchPrescription}
      />
      <PrescriptionMedicineEditModal
        open={medicineEditModalOpen}
        onOpenChange={setMedicineEditModalOpen}
        prescription={prescription}
        onSuccess={() => {
          fetchPrescription();
        }}
        onConflict={fetchPrescription}
      />
    </div>
  );
};

export default PrescriptionDetail;
